// ============================================================================
// CLEO'S SECRET — CleoBrief
// File: src/pages/CleoBrief.jsx
//
// The Cleo Brief is the formal document the GP prepares for the patient after
// their consultation. It presents the clinical pattern, what the body is
// asking for (numbered priorities), and three pathway choices for moving
// forward. The patient responds (enrol / decline / reflect) and that response
// is bound to the brief.
//
// Pattern: PATIENT-FACING EDITORIAL. The "paper" — the inset SAND panel
// holding the brief — is the centrepiece. Wrapped in PAP page chrome with
// the standard editorial header (eyebrow + italic premise + H1 + accent rule).
//
// Already on the May 2026 design system; this pass cleans up:
//   • Inline Cinzel font strings replaced with CINZEL constant
//   • Unused imports removed (T, BtnPrimary)
//   • Sentence case on all titles ("12-week transformation program" etc.)
//   • Selection pattern unified with SelectCard from the rest of the system
//   • Em dashes / decorative glyphs swept
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  P, FF, FS,
  AnkhSm, GeoDivider, BtnGhost, Spinner,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';

// ───────── Helpers ─────────
function formatDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function romanNumeral(n) {
  var map = ['I','II','III','IV','V','VI','VII','VIII','IX','X'];
  return map[n - 1] || String(n);
}

// ═════════════════════════════════════════════════════════════════════════
// CleoBrief
// ═════════════════════════════════════════════════════════════════════════
export default function CleoBrief(props) {
  var onNavigate = props.onNavigate;
  var email = props.email;
  var briefId = props.briefId;

  var briefState = useState(null);
  var brief = briefState[0];
  var setBrief = briefState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var respondingState = useState(false);
  var responding = respondingState[0];
  var setResponding = respondingState[1];

  var emailWaitState = useState(0);
  var emailWaitMs = emailWaitState[0];
  var setEmailWaitMs = emailWaitState[1];

  useEffect(function() {
    if (!email) {
      if (emailWaitMs >= 3000) {
        setError('You need to be logged in to view your brief.');
        setLoading(false);
        return;
      }
      var timer = setTimeout(function() { setEmailWaitMs(emailWaitMs + 500); }, 500);
      return function() { clearTimeout(timer); };
    }
    loadBrief();
  }, [email, briefId, emailWaitMs]);

  function loadBrief() {
    setLoading(true);
    setError(null);
    fetch('/api/get-cleo-brief', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, briefId: briefId || null }),
    })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw new Error(e.error || 'Could not load brief'); });
        return res.json();
      })
      .then(function(result) { setBrief(result); setLoading(false); })
      .catch(function(err) { setError(err.message); setLoading(false); });
  }

  function respondToBrief(response) {
    setResponding(true);
    fetch('/api/respond-to-brief', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, briefId: brief.id, response: response }),
    })
      .then(function(res) { return res.json(); })
      .then(function() { setResponding(false); loadBrief(); })
      .catch(function() { setResponding(false); });
  }

  // ────────────────────────────────────────────────────────────────────────
  // LOADING STATE
  // ────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        background: P.PAP,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO} size={48} />
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // ERROR / NOT-READY STATE
  // ────────────────────────────────────────────────────────────────────────
  if (error || !brief) {
    return (
      <div style={{
        background: P.PAP,
        minHeight: '100vh',
        padding: 'clamp(80px, 12vw, 140px) clamp(24px, 3vw, 40px)',
        textAlign: 'center',
        fontFamily: FF,
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 22,
          }}>Not yet ready</div>

          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(36px, 4.6vw, 56px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1.1,
            letterSpacing: '-0.014em',
            margin: '0 0 clamp(24px, 2.8vw, 36px)',
          }}>
            Your Cleo brief is not yet <span style={{ fontStyle: 'italic', color: P.BR2 }}>available.</span>
          </h1>

          <div style={{
            width: 'clamp(120px, 12vw, 160px)',
            height: 1,
            background: P.GO,
            margin: '0 auto clamp(28px, 3vw, 36px)',
            opacity: 0.85,
          }} />

          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(17px, 1.6vw, 20px)',
            color: P.TL,
            lineHeight: 1.75,
            maxWidth: 500,
            margin: '0 auto clamp(36px, 4vw, 48px)',
          }}>
            {error || 'Your GP will create your brief after your consultation.'}
          </p>

          <BtnGhost onClick={function() { if (onNavigate) onNavigate('portal'); }}>
            Back to portal
          </BtnGhost>
        </div>
      </div>
    );
  }

  var responded = !!brief.patientResponse;

  // ════════════════════════════════════════════════════════════════════════
  // MAIN BRIEF
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ background: P.PAP, minHeight: '100vh', fontFamily: FF }}>

      {/* ═════════ Editorial header ═════════ */}
      <header style={{
        background: P.SAND,
        borderBottom: '1px solid ' + P.SAND3,
        padding: 'clamp(72px, 9vw, 112px) clamp(24px, 3vw, 40px) clamp(56px, 7vw, 88px)',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}>
        <Ankh responsive="opener" color={P.GO} />

        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 13px)',
          fontWeight: 700,
          letterSpacing: '0.34em',
          textTransform: 'uppercase',
          color: P.BR2,
          margin: 'clamp(28px, 3vw, 40px) 0 22px',
        }}>Your Cleo brief</div>

        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(40px, 5.4vw, 64px)',
          fontWeight: 400,
          color: P.INK,
          lineHeight: 1.08,
          letterSpacing: '-0.014em',
          margin: 0,
          maxWidth: 900,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          {brief.patternOneLiner || 'Your clinical pattern.'}
        </h1>

        <div style={{
          width: 'clamp(140px, 14vw, 180px)',
          height: 1,
          background: P.GO,
          margin: 'clamp(24px, 2.6vw, 32px) auto 0',
        }} />
      </header>

      {/* ═════════ Body ═════════ */}
      <main style={{
        maxWidth: 880,
        margin: '0 auto',
        padding: 'clamp(56px, 6.4vw, 88px) clamp(24px, 3vw, 40px) clamp(56px, 6.4vw, 88px)',
      }}>

        {/* ─── The paper ─── */}
        <article style={{
          background: P.SAND,
          padding: 'clamp(40px, 5.4vw, 80px) clamp(28px, 4.2vw, 64px)',
          border: '1px solid ' + P.SAND3,
          boxShadow: '0 4px 0 rgba(201,169,97,0.08), 0 28px 64px -32px rgba(29,17,22,0.15)',
        }}>

          {/* Top ornament */}
          <div style={{ textAlign: 'center', marginBottom: 'clamp(40px, 5vw, 56px)' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <AnkhSm color={P.GO} opacity={0.7} size={28} />
            </div>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(12px, 1vw, 14px)',
              fontWeight: 700,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginTop: 20,
            }}>What your body is asking for</div>
          </div>

          {/* Needs summary — roman-numeral priorities */}
          <div style={{ marginBottom: 'clamp(40px, 5vw, 56px)' }}>
            {(brief.needsSummary || []).map(function(item, i) {
              var isLast = i === (brief.needsSummary || []).length - 1;
              return (
                <div key={i} style={{
                  padding: 'clamp(20px, 2.4vw, 28px) 0',
                  borderBottom: isLast ? 'none' : '1px solid rgba(200,184,136,0.7)',
                  display: 'grid',
                  gridTemplateColumns: 'clamp(48px, 7vw, 72px) 1fr',
                  gap: 'clamp(14px, 2vw, 26px)',
                  alignItems: 'flex-start',
                }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(34px, 4vw, 48px)',
                    fontWeight: 400,
                    color: P.BR,
                    lineHeight: 1,
                    textAlign: 'center',
                    letterSpacing: '-0.01em',
                  }}>{romanNumeral(item.priority || (i + 1))}</div>

                  <div>
                    <h3 style={{
                      fontFamily: FF,
                      fontSize: 'clamp(20px, 2.4vw, 28px)',
                      fontWeight: 500,
                      color: P.INK,
                      lineHeight: 1.3,
                      letterSpacing: '-0.008em',
                      margin: '0 0 12px',
                    }}>{item.body_is_asking_for}</h3>
                    <p style={{
                      fontFamily: FF,
                      fontSize: 'clamp(16px, 1.7vw, 19px)',
                      color: P.TX,
                      lineHeight: 1.85,
                      textAlign: 'justify',
                      WebkitHyphens: 'auto',
                      msHyphens: 'auto',
                      hyphens: 'auto',
                      margin: 0,
                    }}>{item.why_this_matters}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <GeoDivider marginV={48} />

          {/* Pathway intro */}
          <div style={{
            textAlign: 'center',
            marginBottom: 'clamp(28px, 3vw, 40px)',
          }}>
            <div style={{
              fontFamily: FF,
              fontSize: 'clamp(12px, 1vw, 14px)',
              fontWeight: 700,
              letterSpacing: '0.34em',
              textTransform: 'uppercase',
              color: P.BR2,
              marginBottom: 22,
            }}>Your pathway</div>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(24px, 2.8vw, 34px)',
              fontStyle: 'italic',
              fontWeight: 400,
              color: P.INK,
              lineHeight: 1.35,
              letterSpacing: '-0.005em',
              margin: '0 0 16px',
            }}>Three options. All <span style={{ color: P.BR2 }}>legitimate.</span></p>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(16px, 1.7vw, 19px)',
              color: P.TL,
              lineHeight: 1.7,
              maxWidth: 520,
              margin: '0 auto',
            }}>
              Your GP recommended a path based on what was discussed. The decision is yours.
            </p>
          </div>

          {/* Three pathway cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 14,
            marginBottom: 'clamp(28px, 3vw, 40px)',
          }}>

            <PathwayCard
              title="The 12-week transformation program"
              tagline="Structured, with clinical oversight throughout."
              description={
                brief.programTrackVariant
                  ? 'Tailored to your pattern. Weekly modules, mid-program GP review at Week 7, pathology retest at Week 12. The most direct path to measurable change.'
                  : 'Weekly modules tailored to your pattern, mid-program GP review at Week 7, pathology retest at Week 12. The most direct path to measurable change.'
              }
              meta={brief.softHoldStartDate ? ('Soft hold: ' + formatDate(brief.softHoldStartDate)) : null}
              isRecommended={brief.recommendedPathway === 'twelve_week'}
              isSelected={brief.patientResponse === 'enrolled'}
              onChoose={function() { respondToBrief('enrolled'); }}
              ctaLabel="Enrol"
            />

            <PathwayCard
              title="Single consultation pathway"
              tagline="A single follow-up, then independent."
              description="You take everything discussed today and run with it. An optional follow-up consultation in six weeks if you would like to check in. Best if you have the resources and clarity to self-direct from here."
              isRecommended={brief.recommendedPathway === 'single_consult'}
              isSelected={brief.patientResponse === 'declined'}
              onChoose={function() { respondToBrief('declined'); }}
              ctaLabel="Choose this path"
            />

            <PathwayCard
              title="48 hours to reflect"
              tagline="Decide when you are ready. Not before."
              description="Take the time to live with this. Your soft hold protects your preferred start date if you choose the program. There is no right answer to this question. There is your answer."
              isRecommended={brief.recommendedPathway === 'reflection'}
              isSelected={brief.patientResponse === 'reflecting'}
              onChoose={function() { respondToBrief('reflecting'); }}
              ctaLabel="I would like 48 hours"
            />
          </div>

          {/* Response status */}
          {responded && (
            <div style={{
              padding: 'clamp(20px, 2.2vw, 26px) clamp(22px, 2.4vw, 30px)',
              background: 'rgba(201,169,97,0.08)',
              borderLeft: '3px solid ' + P.GO,
              marginTop: 'clamp(28px, 3vw, 40px)',
              textAlign: 'center',
            }}>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(10px, 0.85vw, 12px)',
                fontWeight: 700,
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: P.BR2,
                marginBottom: 14,
              }}>Your decision</div>
              <p style={{
                fontFamily: FF,
                fontSize: 'clamp(16px, 1.6vw, 19px)',
                color: P.TX,
                lineHeight: 1.7,
                margin: '0 0 18px',
              }}>
                {brief.patientResponse === 'enrolled' && 'You have chosen to enrol in the 12-week program. Your GP will be in touch with next steps within twenty-four hours.'}
                {brief.patientResponse === 'declined' && 'You have chosen the single consultation pathway. Your GP has been notified.'}
                {brief.patientResponse === 'reflecting' && 'You have taken 48 hours to decide. We will check in with you. There is no pressure.'}
              </p>
              <button
                onClick={function() { respondToBrief(null); }}
                style={{
                  fontFamily: FS,
                  fontSize: 'clamp(10px, 0.85vw, 12px)',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color: P.BR2,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'color 0.2s',
                }}
                onMouseEnter={function(e) { e.currentTarget.style.color = P.BR; }}
                onMouseLeave={function(e) { e.currentTarget.style.color = P.BR2; }}>
                Change my mind
              </button>
            </div>
          )}

          {responding && (
            <div style={{
              textAlign: 'center',
              marginTop: 28,
              padding: 24,
            }}>
              <Spinner color={P.GO} size={32} />
            </div>
          )}

          {/* Signature */}
          <div style={{
            marginTop: 'clamp(48px, 5.4vw, 72px)',
            paddingTop: 'clamp(28px, 3vw, 40px)',
            borderTop: '1px solid ' + P.SAND3,
            textAlign: 'center',
          }}>
            <p style={{
              fontFamily: FF,
              fontSize: 'clamp(13px, 1.2vw, 15px)',
              fontStyle: 'italic',
              color: P.TF,
              lineHeight: 1.7,
              maxWidth: 500,
              margin: '0 auto',
            }}>
              Prepared by your GP at Cleo's Secret.
              {brief.gpApprovedAt && (' Reviewed and approved ' + formatDate(brief.gpApprovedAt) + '.')}
            </p>
            <div style={{ marginTop: 22, display: 'flex', justifyContent: 'center' }}>
              <AnkhSm color={P.GO} opacity={0.55} size={24} />
            </div>
          </div>

        </article>

        {/* ─── Below-paper actions ─── */}
        <div style={{
          marginTop: 'clamp(36px, 4vw, 52px)',
          display: 'flex',
          justifyContent: 'center',
          gap: 14,
          flexWrap: 'wrap',
        }}>
          {brief.briefPdfUrl && (
            <BtnGhost onClick={function() { window.open(brief.briefPdfUrl, '_blank'); }}>
              Download as PDF
            </BtnGhost>
          )}
          <BtnGhost onClick={function() { window.print(); }}>Print this brief</BtnGhost>
          <BtnGhost onClick={function() { if (onNavigate) onNavigate('portal'); }}>Back to portal</BtnGhost>
        </div>

        {/* ─── Disclaimer ─── */}
        <div style={{
          marginTop: 'clamp(36px, 4vw, 52px)',
          textAlign: 'center',
          maxWidth: 600,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(13px, 1.1vw, 14px)',
            fontStyle: 'italic',
            color: P.TF,
            lineHeight: 1.75,
            margin: 0,
          }}>
            This brief summarises a conversation with your GP and reflects the clinical thinking discussed. It is not a substitute for ongoing care. For emergencies, call 000.
          </p>
        </div>

      </main>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: ${P.SAND} !important; }
          button { display: none !important; }
        }
      `}</style>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// PathwayCard — selection card matching SelectCard pattern from the
// transactional pages (CleoScheduler, PathologyUpload)
// ═════════════════════════════════════════════════════════════════════════
function PathwayCard({ title, tagline, description, meta, isRecommended, isSelected, onChoose, ctaLabel }) {
  // Border + bg follow the SelectCard convention:
  //   selected → GO border + gold-tint bg + 3px GO left bar
  //   recommended (not selected) → GO border + 3px GO left bar (no tint)
  //   default → SAND3 border + 3px SAND3 left bar
  var borderColor = isSelected ? P.GO : (isRecommended ? P.GO : P.SAND3);
  var leftBarColor = (isSelected || isRecommended) ? P.GO : P.SAND3;
  var bgColor = isSelected ? 'rgba(201,169,97,0.08)' : P.PAP;

  return (
    <div style={{
      padding: 'clamp(26px, 3.4vw, 40px)',
      background: bgColor,
      border: '1px solid ' + borderColor,
      borderLeft: '3px solid ' + leftBarColor,
      transition: 'all 0.2s',
      position: 'relative',
    }}>
      {/* Badge + title row — flex so badge stacks above title on narrow widths */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        flexWrap: 'wrap-reverse',
        gap: 'clamp(8px, 1vw, 14px)',
        marginBottom: 10,
      }}>
        <h3 style={{
          fontFamily: FF,
          fontSize: 'clamp(22px, 2.4vw, 28px)',
          fontWeight: 500,
          color: P.INK,
          lineHeight: 1.2,
          letterSpacing: '-0.008em',
          margin: 0,
          flex: '1 1 220px',
        }}>{title}</h3>

        {isRecommended && !isSelected && (
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(10px, 0.85vw, 12px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            padding: '5px 12px',
            background: 'rgba(201,169,97,0.12)',
            border: '1px solid ' + P.GO,
            flexShrink: 0,
            alignSelf: 'flex-start',
          }}>GP recommended</div>
        )}
        {isSelected && (
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(10px, 0.85vw, 12px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.GREEN,
            padding: '5px 12px',
            background: P.GREEN_BG,
            border: '1px solid ' + P.GREEN,
            flexShrink: 0,
            alignSelf: 'flex-start',
          }}>Your choice</div>
        )}
      </div>

      <p style={{
        fontFamily: FF,
        fontSize: 'clamp(16px, 1.7vw, 20px)',
        fontStyle: 'italic',
        color: P.BR2,
        lineHeight: 1.45,
        margin: '0 0 18px',
      }}>{tagline}</p>

      <p style={{
        fontFamily: FF,
        fontSize: 'clamp(15px, 1.4vw, 17px)',
        color: P.TX,
        lineHeight: 1.85,
        textAlign: 'justify',
        WebkitHyphens: 'auto',
        msHyphens: 'auto',
        hyphens: 'auto',
        margin: '0 0 20px',
      }}>{description}</p>

      {meta && (
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 13px)',
          fontWeight: 700,
          letterSpacing: '0.34em',
          textTransform: 'uppercase',
          color: P.BR2,
          marginBottom: 20,
        }}>{meta}</div>
      )}

      {!isSelected && (
        <BtnGhost onClick={onChoose}>{ctaLabel}</BtnGhost>
      )}
    </div>
  );
}
